import "./SearchBar.scss";
import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { ReactComponent as SearchIcon } from "./../../assets/icons/search-icon.svg";
import { debounce } from "lodash";

interface SearchItem {
  id: string;
  title: string;
  subtitle?: string;
}

interface SearchProps {
  onSearch: (query: string) => void;
  delay?: number;
  showClearButton?: boolean;
  placeholder?: string;
  initialQuery?: string;
  // Global search props
  jobs?: SearchItem[];
  containers?: SearchItem[];
  onItemSelect?: (item: SearchItem, type: "job" | "container") => void;
  enableGlobalSearch?: boolean;
  // Suggestion-based search props
  showSuggestions?: boolean;
  suggestions?: string[];
  onSuggestionClick?: (suggestion: string) => void;
}

const SearchBar: React.FC<SearchProps> = ({
  onSearch,
  delay = 300,
  showClearButton = false,
  placeholder,
  initialQuery = "",
  jobs = [],
  containers = [],
  onItemSelect,
  enableGlobalSearch = false,
  showSuggestions = false,
  suggestions = [],
  onSuggestionClick,
}) => {
  const [inputFocus, setInputFocus] = useState<boolean>(false);
  const [query, setQuery] = useState(initialQuery);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Memoize the debounced search function with cleanup
  const debouncedSearch = useMemo(() => {
    const debounced = debounce((nextQuery: string) => {
      if (nextQuery.trim() === "") {
        onSearch("");
        return;
      }
      onSearch(nextQuery);
    }, delay);
    return debounced;
  }, [onSearch, delay]);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setQuery(value);
      debouncedSearch(value);
      if (enableGlobalSearch || showSuggestions) {
        setShowDropdown(value.length > 0);
      }
    },
    [debouncedSearch, enableGlobalSearch, showSuggestions]
  );

  const handleClear = useCallback(() => {
    setQuery("");
    onSearch("");
    setShowDropdown(false);
  }, [onSearch]);

  const handleItemClick = useCallback(
    (item: SearchItem, type: "job" | "container") => {
      setQuery(item.title);
      if (onItemSelect) {
        onItemSelect(item, type);
      }
      setShowDropdown(false);
    },
    [onItemSelect]
  );

  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      setQuery(suggestion);
      if (onSuggestionClick) {
        onSuggestionClick(suggestion);
      }
      setShowDropdown(false);
    },
    [onSuggestionClick]
  );

  const filterItems = useCallback(
    (items: SearchItem[]) => {
      return items.filter(
        (item) =>
          item.title.toLowerCase().includes(query.toLowerCase()) ||
          (item.subtitle &&
            item.subtitle.toLowerCase().includes(query.toLowerCase()))
      );
    },
    [query]
  );

  // Memoize filtered results
  const filteredJobs = useMemo(() => filterItems(jobs), [jobs, filterItems]);
  const filteredContainers = useMemo(
    () => filterItems(containers),
    [containers, filterItems]
  );
  const filteredSuggestions = useMemo(
    () =>
      suggestions.filter((suggestion) =>
        suggestion.toLowerCase().includes(query.toLowerCase())
      ),
    [suggestions, query]
  );

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchBarRef.current &&
        !searchBarRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    if (enableGlobalSearch || showSuggestions) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [enableGlobalSearch, showSuggestions]);

  // Set initial query
  useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  // Cleanup blur timeout on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  const renderDropdown = useCallback(() => {
    if (!showDropdown) return null;

    if (
      enableGlobalSearch &&
      (filteredJobs.length > 0 || filteredContainers.length > 0)
    ) {
      return (
        <div className="search-suggestions">
          {filteredJobs.length > 0 && (
            <div className="suggestion-section">
              <div className="section-header">Jobs</div>
              {filteredJobs.map((job) => (
                <div
                  key={job.id}
                  className="suggestion-item"
                  onClick={() => handleItemClick(job, "job")}
                >
                  <div className="item-title">{job.title}</div>
                  {job.subtitle && (
                    <div className="item-subtitle">{job.subtitle}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {filteredContainers.length > 0 && (
            <div className="suggestion-section">
              <div className="section-header">Containers</div>
              {filteredContainers.map((container) => (
                <div
                  key={container.id}
                  className="suggestion-item"
                  onClick={() => handleItemClick(container, "container")}
                >
                  <div className="item-title">{container.title}</div>
                  {container.subtitle && (
                    <div className="item-subtitle">{container.subtitle}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (showSuggestions && filteredSuggestions.length > 0) {
      return (
        <div className="search-suggestions">
          <div className="suggestion-section">
            {filteredSuggestions.map((suggestion, index) => (
              <div
                key={index}
                className="suggestion-item"
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <div className="item-title">{suggestion}</div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return null;
  }, [
    showDropdown,
    enableGlobalSearch,
    filteredJobs,
    filteredContainers,
    showSuggestions,
    filteredSuggestions,
    handleItemClick,
    handleSuggestionClick,
  ]);

  return (
    <div className="search-bar-component" ref={searchBarRef}>
      <input
        type={showClearButton ? "search" : "text"}
        value={query}
        onChange={handleChange}
        placeholder={
          placeholder
            ? placeholder
            : enableGlobalSearch
            ? "Search jobs and containers..."
            : "Search..."
        }
        onFocus={() => {
          setInputFocus(true);
          if ((enableGlobalSearch || showSuggestions) && query.length > 0) {
            setShowDropdown(true);
          }
        }}
        onBlur={() => {
          if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
          }
          blurTimeoutRef.current = setTimeout(() => {
            setInputFocus(false);
          }, 200);
        }}
      />
      <button
        className={inputFocus ? "focused-btn" : "not-focused-btn"}
        onClick={showClearButton && query ? handleClear : undefined}
      >
        <SearchIcon />
      </button>
      {renderDropdown()}
    </div>
  );
};

export default SearchBar;
